import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { apiFetch } from '../lib/api';
import toast from 'react-hot-toast';
import emailjs from 'emailjs-com';
import { logger } from '../utils/logger';
import { sanitizeInput, sanitizeEmail } from '../utils/sanitizer';
import { sanitizeError } from '../utils/errorHandler';
import { 
  Smartphone, 
  Users, 
  Clock, 
  MessageSquare, 
  Plus,
  Calendar,
  Globe,
  Bell,
  ArrowRight,
  Sparkles
} from 'lucide-react';

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const Dashboard = () => {
  const { user } = useAuth();
  const { isExpanded } = useSidebar();
  const [stats, setStats] = useState({
    myApps: 0,
    myApplications: 0,
    activeTests: 0,
    unreadMessages: 0
  });
  const [allApps, setAllApps] = useState([]);
  const [myApps, setMyApps] = useState([]);
  const [myApplicationsList, setMyApplicationsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [email, setEmail] = useState('');

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Beklemede';
      case 'approved':
      case 'accepted':
        return 'Kabul Edildi';
      case 'rejected':
        return 'Reddedildi';
      case 'completed':
        return 'Tamamlandı';
      default:
        return status;
    }
  };

  useEffect(() => {
    // Supabase'den gerçek verileri çek
    const fetchData = async () => {
      try {
        if (!user) {
          setLoading(false);
          return;
        }

        // Kullanıcının uygulamalarını çek - Worker üzerinden
        const myAppsResponse = await apiFetch('/api/my-apps');
        const myApps = myAppsResponse.ok ? await myAppsResponse.json() : [];

        // Kullanıcının başvurularını çek - Worker üzerinden
        const myApplicationsResponse = await apiFetch('/api/applications?select=*');
        let myApplications = myApplicationsResponse.ok ? await myApplicationsResponse.json() : [];
        // Her başvuru için app detayını ekle (başlık/platform için)
        myApplications = await Promise.all(
          (myApplications || []).map(async (appl) => {
            try {
              const appRes = await apiFetch(`/api/apps?id=eq.${appl.app_id}&select=id,title,platform,description`);
              const appData = await appRes.json();
              return { ...appl, app: appData?.[0] || null };
            } catch (_) {
              return { ...appl, app: null };
            }
          })
        );

        // Tüm uygulamaları çek - Worker üzerinden
        const allAppsResponse = await apiFetch('/api/apps?select=*');
        const allApps = allAppsResponse.ok ? await allAppsResponse.json() : [];

        // Her uygulama için creator bilgisini çek - Worker üzerinden
        if (allApps && allApps.length > 0) {
          const uniqueUserIds = [...new Set(allApps.map(app => app.user_id))];
          
          // Her user_id için ayrı ayrı profile çek
          const profiles = await Promise.all(
            uniqueUserIds.map(async (userId) => {
              try {
                const response = await apiFetch(`/api/profiles?id=eq.${userId}&select=id,first_name,last_name,email`);
                const data = await response.json();
                return data[0] || null;
              } catch (error) {
                console.error('Profile fetch error:', error);
                return null;
              }
            })
          );

          // Creator bilgilerini uygulamalara ekle
          allApps.forEach(app => {
            const creator = profiles.find(p => p && p.id === app.user_id);
            app.creator = creator || null;
          });
        }

        // Stats güncelle (unreadMessages'i ezme!)
        setStats(prev => ({
          ...prev,
          myApps: myApps?.length || 0,
          myApplications: myApplications?.length || 0,
          // Tester için aktif test: kabul edilmiş başvurular
          activeTests: myApplications?.filter(a => ['accepted','approved'].includes(a.status)).length || 0,
        }));
        
        // Uygulamaları tarihe göre azalan sırada sırala (en yeni en üstte)
        const sortedAllApps = (allApps || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        
        setAllApps(sortedAllApps);
        setMyApps(myApps || []);
        setMyApplicationsList(myApplications || []);
        setLoading(false);
      } catch (error) {
        logger.error('Veri çekme hatası:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Unread mesaj sayısını ayrı izleyelim ve dış tetikleyiciyle güncelleyelim
  useEffect(() => {
    const refreshUnread = async () => {
      if (!user?.id) return;
      try {
        const response = await apiFetch(`/api/messages?select=id&receiver_id=eq.${user.id}&is_read=eq.false`);
        const data = await response.json();
        setStats(prev => ({ ...prev, unreadMessages: data?.length || 0 }));
      } catch (_) {}
    };

    refreshUnread();
    const handler = () => refreshUnread();
    window.addEventListener('refresh-unread', handler);

    return () => {
      window.removeEventListener('refresh-unread', handler);
    };
  }, [user?.id]);

  const handleApply = (appId) => {
    // Kendi uygulamanıza başvuru yapılamaz kontrolü
    const app = allApps.find(app => app.id === appId);
    if (app && app.user_id === user?.id) {
      toast.error('Kendi uygulamanıza başvuru yapamazsınız!');
      return;
    }
    
    // Mail modal'ı aç
    setSelectedApp(appId);
    setShowEmailModal(true);
  };

  const handleAppClick = (appId) => {
    // Uygulama detayına git
    window.location.href = `/app/${appId}`;
  };

  const handleEmailSubmit = async () => {
    try {
      
      if (!selectedApp || !email) {
        
        return;
      }

      // Basit rate limit: 60 sn içinde tekrar gönderimi engelle (DB insert öncesi)
      const now = Date.now();
      const last = Number(localStorage.getItem('email_notify_last_ts') || 0);
      const diffMs = now - last;
      if (diffMs < 180_000) {
        const remain = Math.ceil((180_000 - diffMs) / 1000);
        toast.error(`Lütfen ${remain} sn sonra tekrar deneyin.`);
        return;
      }

      // Duplicate application kontrolü
      try {
        const response = await apiFetch(`/api/applications?select=id&app_id=eq.${selectedApp}&user_id=eq.${user.id}`);
        const existingApplication = await response.json();

        if (existingApplication && existingApplication.length > 0) {
          toast.error('Bu uygulamaya zaten başvuru yaptınız!');
          setShowEmailModal(false);
          setSelectedApp(null);
          setEmail('');
          return;
        }
      } catch (error) {
        // 404 ise devam et, başka hata ise fırlat
        if (error.message.includes('404')) {
          // Devam et
        } else {
          throw error;
        }
      }

      // API'ye başvuru kaydet
      const response = await apiFetch('/api/applications', {
        method: 'POST',
        body: JSON.stringify({
          app_id: selectedApp,
          user_id: user.id,
          email: sanitizeEmail(email),
          status: 'pending'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Başvuru kaydedilemedi');
      }

      // Uygulama sahibine EmailJS ile e-posta bildirimi gönder (Türkçe)
      try {
        const app = allApps.find(a => a.id === selectedApp);
        if (app && app.creator?.email) {
          await emailjs.send(
            EMAILJS_SERVICE_ID,
            EMAILJS_TEMPLATE_ID,
            {
              to_email: app.creator.email,
              subject: `Yeni Tester Başvurusu • ${app.title}`,
              from_name: '12Tester Bildirim',
              from_email: user.email,
              title: app.title,
              app_url: `https://12tester.com.tr/app/${app.id}`,
              message: `${user.email} kullanıcısı ${app.title} uygulamanıza tester olarak başvurdu.`
            },
            EMAILJS_PUBLIC_KEY
          );
          localStorage.setItem('email_notify_last_ts', String(now));
        }
      } catch (e) {
        logger.error('Başvuru maili gönderilemedi', e);
      }

      setShowEmailModal(false);
      setSelectedApp(null);
      setEmail('');
      toast.success('Başvurunuz başarıyla gönderildi!');
    } catch (error) {
      logger.error('Başvuru gönderilirken hata:', error);
      toast.error(sanitizeError(error));
    }
  };

  const handleMessage = (creator, appId) => {
    // Kullanıcının profil sayfasına yönlendir
    window.location.href = `/profile/${creator.id}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="relative">
          <div className="animate-spin rounded-full h-32 w-32 border-b-4 border-emerald-600"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <Sparkles className="h-8 w-8 text-emerald-600 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600">
      {/* Mobile Header Gradient */}
      <div className="md:hidden relative h-48">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-emerald-400/20 rounded-full blur-3xl"></div>
        </div>
      </div>

      {/* Desktop Header Gradient */}
      <div className="hidden md:block relative h-48">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-emerald-400/20 rounded-full blur-3xl"></div>
        </div>
      </div>

      {/* Main Content */}
      <main className={`relative md:-mt-32 -mt-32 pb-4 transition-all duration-300 ${
        isExpanded ? 'md:ml-72' : 'md:ml-18'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Welcome Section */}
          <div className="mb-4">
            <div className="backdrop-blur-xl bg-white/20 rounded-2xl p-4 shadow-xl border border-white/30">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center shadow-lg">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">
                    Hoş geldiniz, {user?.user_metadata?.first_name || user?.email?.split('@')[0]}!
                  </h1>
                  <p className="text-white/80 text-sm">
                    12Tester platformunda uygulama test süreçlerinizi yönetin
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="backdrop-blur-xl bg-white/60 rounded-xl shadow-lg p-3 border border-white/40">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center shadow-md mr-2">
                  <Smartphone className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600">Uygulamalarım</p>
                  <p className="text-xl font-bold text-gray-900">{stats.myApps}</p>
                </div>
              </div>
            </div>

            <div className="backdrop-blur-xl bg-white/60 rounded-xl shadow-lg p-3 border border-white/40">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gradient-to-r from-teal-500 to-teal-600 rounded-lg flex items-center justify-center shadow-md mr-2">
                  <Users className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600">Başvurularım</p>
                  <p className="text-xl font-bold text-gray-900">{stats.myApplications}</p>
                </div>
              </div>
            </div>

            <div className="backdrop-blur-xl bg-white/60 rounded-xl shadow-lg p-3 border border-white/40">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-lg flex items-center justify-center shadow-md mr-2">
                  <Clock className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600">Aktif Testler</p>
                  <p className="text-xl font-bold text-gray-900">{stats.activeTests}</p>
                </div>
              </div>
            </div>

            <div className="backdrop-blur-xl bg-white/60 rounded-xl shadow-lg p-3 border border-white/40">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gradient-to-r from-emerald-600 to-teal-500 rounded-lg flex items-center justify-center shadow-md mr-2">
                  <MessageSquare className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600">Okunmamış Mesajlar</p>
                  <p className="text-xl font-bold text-gray-900">{stats.unreadMessages}</p>
                </div>
              </div>
            </div>
          </div>

          {/* All Apps Section */}
          <div className="backdrop-blur-xl bg-white/60 rounded-xl shadow-lg mb-4 border border-white/40">
            <div className="px-4 py-3 border-b border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Tüm Uygulamalar</h2>
                  <p className="text-xs text-gray-600">Diğer kullanıcıların uygulamalarına başvuru yapabilirsiniz</p>
                </div>
                <Link
                  to="/my-apps"
                  className="group inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-medium rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300 text-sm"
                >
                  <Plus className="h-3 w-3 mr-1 group-hover:rotate-90 transition-transform" />
                  Yeni Uygulama
                </Link>
              </div>
            </div>
            
            <div className="p-4 md:p-6">
              <div className="space-y-3 md:space-y-4">
                {allApps.map((app) => (
                  <div 
                    key={app.id} 
                    className="relative backdrop-blur-sm bg-white/40 rounded-lg p-3 md:p-6 cursor-pointer hover:bg-white/60 transition-all duration-300 border border-white/30 hover:shadow-md group"
                    onClick={() => handleAppClick(app.id)}
                  >
                    {/* Mobil tarih badge */}
                    <div className="absolute top-2 right-2 text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 md:hidden">
                      {new Date(app.created_at).toLocaleDateString('tr-TR')}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex-1 pr-4">
                        {/* Başlık ve platform */}
                        <div className="flex items-center space-x-3 mb-2 md:mb-3">
                          <h3 className="text-sm md:text-lg font-semibold text-gray-900 group-hover:text-emerald-700 transition-colors leading-tight">
                            {app.title}
                          </h3>
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs md:text-sm font-medium bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-800 border border-emerald-200 whitespace-nowrap">
                            {app.platform}
                          </span>
                        </div>
                        
                        {/* Alt bilgiler */}
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs md:text-sm text-gray-600">
                          {/* Desktop tarih */}
                          <div className="hidden md:flex items-center">
                            <Calendar className="h-4 w-4 mr-2 text-teal-600" />
                            <span className="font-medium">{new Date(app.created_at).toLocaleDateString('tr-TR')}</span>
                          </div>
                          
                          {/* Tester sayısı */}
                          <div className="flex items-center">
                            <Users className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2 text-cyan-600" />
                            <span className="font-semibold">{app.current_testers || 0}/{app.max_testers || 12}</span>
                          </div>
                          
                          {/* Oluşturan */}
                          <div className="flex items-center min-w-0">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (app.creator) handleMessage(app.creator, app.id);
                              }}
                              className="text-emerald-700 font-medium hover:text-emerald-800 hover:underline cursor-pointer transition-colors truncate"
                            >
                              <span className="md:hidden">
                                {app.creator 
                                  ? (app.creator.first_name || app.creator.email?.split('@')[0]?.substring(0, 10) || 'Bilinmiyor')
                                  : 'Bilinmiyor'}
                              </span>
                              <span className="hidden md:inline">
                                Oluşturan: {app.creator 
                                  ? `${app.creator.first_name || app.creator.email?.split('@')[0] || ''} ${app.creator.last_name || ''}`.trim()
                                  : 'Bilinmiyor'}
                              </span>
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Butonlar */}
                      <div className="flex flex-col items-end md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-3 min-w-0 mt-6 md:mt-0">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAppClick(app.id);
                          }}
                          className="inline-flex items-center justify-center px-2 py-1 md:px-4 md:py-2 border border-gray-300 text-[11px] md:text-sm font-medium rounded-md md:rounded-lg text-gray-700 bg-white/70 hover:bg-white/90 hover:border-gray-400 transition-all duration-300 whitespace-nowrap"
                        >
                          Detay
                        </button>
                        {app.user_id !== user?.id && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApply(app.id);
                            }}
                            className="inline-flex items-center justify-center px-2 py-1 md:px-4 md:py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-medium rounded-md md:rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300 text-[11px] md:text-sm whitespace-nowrap"
                          >
                            Başvur
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="backdrop-blur-xl bg-white/60 rounded-xl shadow-lg p-4 border border-white/40">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Uygulamalarım</h3>
                  <p className="text-xs text-gray-600">Uygulamalarınızı yönetin ve test süreçlerini takip edin</p>
                </div>
                <Link
                  to="/my-apps"
                  className="group inline-flex items-center px-2 py-1 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-medium rounded text-xs shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                >
                  <Plus className="h-3 w-3 mr-1 group-hover:rotate-90 transition-transform" />
                  Yeni Uygulama
                </Link>
              </div>
              <div>
                {myApps.length > 0 ? (
                  <div className="space-y-2">
                    {myApps.slice(0, 2).map((app) => (
                      <div key={app.id} className="backdrop-blur-sm bg-white/40 rounded p-2 border border-white/30">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900 text-xs">{app.title}</h4>
                            <p className="text-xs text-gray-600">{app.platform}</p>
                          </div>
                          <span className="text-xs font-medium text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full">
                            {app.current_testers || 0}/{app.max_testers || 12} tester
                          </span>
                        </div>
                      </div>
                    ))}
                    {myApps.length > 2 && (
                      <Link
                        to="/my-apps"
                        className="text-xs text-emerald-700 hover:text-emerald-800 flex items-center font-medium"
                      >
                        +{myApps.length - 2} daha fazla uygulama
                        <ArrowRight className="h-3 w-3 ml-1" />
                      </Link>
                    )}
                  </div>
                ) : (
                  <Link
                    to="/my-apps"
                    className="text-xs text-emerald-700 hover:text-emerald-800 flex items-center font-medium"
                  >
                    Tüm uygulamaları görüntüle
                    <ArrowRight className="h-3 w-3 ml-1" />
                  </Link>
                )}
              </div>
            </div>

            <div className="backdrop-blur-xl bg-white/60 rounded-xl shadow-lg p-4 border border-white/40">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Başvurularım</h3>
                  <p className="text-xs text-gray-600">Tester olarak başvurduğunuz uygulamaları takip edin</p>
                </div>
                <Link
                  to="/my-applications"
                  className="group inline-flex items-center px-2 py-1 bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-medium rounded text-xs shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                >
                  Teste Hazır Uygulamalar
                </Link>
              </div>
              <div>
                {myApplicationsList && myApplicationsList.length > 0 ? (
                  <div className="space-y-2">
                    {myApplicationsList.slice(0, 2).map((appl) => (
                      <div key={appl.id} className="backdrop-blur-sm bg-white/40 rounded p-2 border border-white/30">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900 text-xs">{appl.app?.title || 'Bilinmeyen Uygulama'}</h4>
                            <p className="text-xs text-gray-600">{appl.app?.platform || 'Platform yok'} · {new Date(appl.created_at).toLocaleDateString('tr-TR')}</p>
                          </div>
                          <span className="text-xs font-medium text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full capitalize">
                            {getStatusText(appl.status || 'pending')}
                          </span>
                        </div>
                      </div>
                    ))}
                    {myApplicationsList.length > 2 && (
                      <Link
                        to="/my-applications"
                        className="text-xs text-emerald-700 hover:text-emerald-800 flex items-center font-medium"
                      >
                        +{myApplicationsList.length - 2} daha fazla başvuru
                        <ArrowRight className="h-3 w-3 ml-1" />
                      </Link>
                    )}
                  </div>
                ) : (
                  <Link
                    to="/my-applications"
                    className="text-xs text-emerald-700 hover:text-emerald-800 flex items-center font-medium"
                  >
                    Tüm başvuruları görüntüle
                    <ArrowRight className="h-3 w-3 ml-1" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="backdrop-blur-xl bg-white/90 border border-white/30 shadow-2xl rounded-2xl p-6 w-full max-w-md">
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">
                E-posta Adresinizi Girin
              </h3>
              <p className="text-gray-600 mb-4 leading-relaxed text-sm">
                Lütfen uygulama sahibinin Google Play Store'daki test kullanıcıları listesine ekleyeceği e-posta adresinizi girin. Bu adres, test sürümüne erişiminiz için kullanılacaktır.
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  E-posta
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ornek@email.com"
                  className="w-full px-3 py-2 backdrop-blur-sm bg-white/60 border border-white/40 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowEmailModal(false)}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white/60 hover:bg-white/80 hover:border-gray-400 transition-all duration-300"
                >
                  İptal
                </button>
                <button
                  onClick={handleEmailSubmit}
                  disabled={!email}
                  className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  Başvur
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;